import {Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef} from '@angular/core';
import {User} from '../../../_models/user';
import {ShippingAddress} from '../../../_models/shipping-address';
import {UserService} from '../../../_services/user.service';
import {DocumentService} from '../../../_services/document.service';
import { AppConstants } from 'src/app/_constants/constants';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import {VerificationModalComponent} from '../verification-modal/verification-modal.component';

@Component({
    selector: 'app-farmer-account',
    templateUrl: './farmer-account.component.html',
    styleUrls: ['./farmer-account.component.css'],
    standalone: false
})
export class FarmerAccountComponent implements OnInit, OnDestroy {

  @ViewChild(VerificationModalComponent) verificationModal!: VerificationModalComponent;

  user: any;
  defaultShippingAddress: ShippingAddress;
  loading = true;
  private subscriptions: Subscription[] = [];

  // Modal visibility states
  showPaymentModal = false;
  showVerificationModal = false;
  showCompanyInfoModal = false;

  // Payment edit modal properties
  existingPaymentInfo: any = null;

  // Company info edit modal properties
  existingCompanyInfo: any = null;

  // Confirmation modal properties
  showConfirmModal = false;
  confirmModalTitle = '';
  confirmModalMessage = '';
  confirmModalAction: (() => void) | null = null;

  // Document upload modal properties
  showUploadModal = false;
  uploadDocumentType: 'registration' | 'identity' = 'registration';
  sellerId: string = '';

  // Company document upload modal properties
  showCompanyDocumentUploadModal = false;
  currentUploadDocType: 'identity' | 'registration' | 'tax' | 'incorporation' = 'identity';
  companyTaxDeliveryDate: string = '';
  companyDocumentFile: File | null = null;
  companyDocumentUploading = false;

  // Verification modal properties
  verificationToken: string | null = null;
  resendCountdown = 0;
  resendDisabled = false;
  verificationError = '';
  private countdownSubscription: Subscription | null = null;

  // Delete account modal properties
  showDeleteAccountModal = false;
  deleteAccountConfirmationText = '';
  deleteAccountLoading = false;
  readonly DELETE_CONFIRMATION_WORD = 'DELETE';

  // Constants for payment types
  readonly PAYMENT_TYPE_MOBILE_MONEY = 'MOBILE_MONEY';
  readonly PAYMENT_TYPE_BANK = 'BANK';

  // Fresh carrier details from endpoint (used for status checks)
  carrierDetailsFromEndpoint: any = null;

  // Fresh seller details from endpoint (used for status checks)
  sellerDetailsFromEndpoint: any = null;

  constructor(
    private userService: UserService,
    private documentService: DocumentService,
    private toast: ToastrService,
    private translateService: TranslateService,
    private cdRef: ChangeDetectorRef
  ) {
    this.user = JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_KEY) ?? '{}');
  }

  ngOnInit() {
    console.log('=== FarmerAccountComponent ngOnInit ===');
    console.log('Initial user from localStorage:', this.user);
    this.loadUserData();
  }

  private loadUserData(): void {
    this.userService.load().subscribe(
      (user: User) => {
        this.user = user;
        this.defaultShippingAddress = user.defaultShippingAddress;

        // Fallback: derive country from shipping address list if user.country is null
        if (!this.user?.country && this.user?.shippingAddressList?.length > 0) {
          this.user.country = this.user.shippingAddressList[0].country;
        }

        // If seller (private or company), load fresh seller account data to get numberVerified status
        if (this.user?.role === 'seller') {
          this.loadSellerAccount();
        }

        // If carrier, load fresh carrier details
        console.log('Checking if carrier:', { role: this.user?.role });
        if (this.user?.role === 'carrier') {
          console.log('User is carrier, calling loadCarrierDetails()');
          this.loadCarrierDetails();
        }
      },
      (error) => {
        this.toast.error(error);
      },
      () => {
        // Only set loading to false if user is NOT a carrier or seller
        // Carrier/seller loading is managed by their respective loaders to avoid flicker
        if (this.user?.role !== 'carrier' && this.user?.role !== 'seller') {
          this.loading = false;
        }
      }
    );
  }

  loadSellerAccount(): void {
    if (!this.user?.id) return;

    const sub = this.userService.getSellerAccount(this.user.id).subscribe({
      next: (response: any) => {
        console.log('[SELLER-LOAD] Seller account response:', response);
        if (response?.data) {
          // Store fresh seller data from endpoint
          this.sellerDetailsFromEndpoint = response.data;
          console.log('[SELLER-LOAD] Seller ID:', this.sellerDetailsFromEndpoint?.id);
          console.log('[SELLER-LOAD] Seller Type:', this.sellerDetailsFromEndpoint?.sellerType);
          console.log('[SELLER-LOAD] Documents count:', this.sellerDetailsFromEndpoint?.documents?.length || 0);
          console.log('[SELLER-LOAD] Documents:', this.sellerDetailsFromEndpoint?.documents);
          
          // Restore seller data for backward compatibility (sidebar/topbar checks)
          this.user.seller = this.sellerDetailsFromEndpoint;
          this.userService.setCurrentUser(this.user);
          
          // Force change detection to update the view
          this.cdRef.detectChanges();
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading seller account:', error);
        this.sellerDetailsFromEndpoint = null;
        this.loading = false;
        this.cdRef.detectChanges();
      }
    });

    this.subscriptions.push(sub);
  }

  loadCarrierDetails(): void {
    console.log('=== loadCarrierDetails() called ===');
    if (!this.user?.id) {
      console.log('No user ID, returning early');
      return;
    }

    console.log('Fetching carrier details for user ID:', this.user.id);
    
    const sub = this.userService.getCarrierDetails(this.user.id).subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.carrierDetailsFromEndpoint = response.data;
          this.user.carrier = this.carrierDetailsFromEndpoint;
          this.userService.setCurrentUser(this.user);
        } else {
          this.carrierDetailsFromEndpoint = null;
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading carrier details:', error);
        // On error, clear the endpoint data so status checks return false
        this.carrierDetailsFromEndpoint = null;
        // Always set loading to false even on error
        this.loading = false;
      }
    });

    this.subscriptions.push(sub);
  }

  // Modal methods for payment editing
  openAddPaymentModal(): void {
    console.log('[PAYMENT-MODAL] openAddPaymentModal called');
    console.log('[PAYMENT-MODAL] this.user:', this.user);
    console.log('[PAYMENT-MODAL] this.sellerDetailsFromEndpoint:', this.sellerDetailsFromEndpoint);
    console.log('[PAYMENT-MODAL] Seller type:', this.sellerDetailsFromEndpoint?.sellerType);
    this.existingPaymentInfo = null;
    this.showPaymentModal = true;
  }

  openEditPaymentModal(): void {
    console.log('[PAYMENT-MODAL] openEditPaymentModal called');
    console.log('[PAYMENT-MODAL] Current paymentInfo:', this.sellerDetailsFromEndpoint?.paymentInfo);
    this.existingPaymentInfo = this.sellerDetailsFromEndpoint?.paymentInfo;
    this.showPaymentModal = true;
  }

  closePaymentModal(): void {
    this.showPaymentModal = false;
    this.existingPaymentInfo = null;
  }

  // Modal methods for company info editing
  openEditCompanyInfoModal(): void {
    this.existingCompanyInfo = this.sellerDetailsFromEndpoint;
    this.showCompanyInfoModal = true;
  }

  closeCompanyInfoModal(): void {
    this.showCompanyInfoModal = false;
    this.existingCompanyInfo = null;
  }

  // Confirmation modal methods
  openConfirmModal(title: string, message: string, action: () => void): void {
    this.confirmModalTitle = title;
    this.confirmModalMessage = message;
    this.confirmModalAction = action;
    this.showConfirmModal = true;
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
    this.confirmModalTitle = '';
    this.confirmModalMessage = '';
    this.confirmModalAction = null;
  }

  onConfirmAction(): void {
    if (this.confirmModalAction) {
      this.confirmModalAction();
    }
    this.closeConfirmModal();
  }

  onSaveCompanyInfo(companyData: any): void {
    if (!this.user?.id || !this.sellerDetailsFromEndpoint) {
      this.toast.error('User data not available');
      return;
    }

    // Build payload with new company data and existing paymentInfo
    const seller = this.sellerDetailsFromEndpoint;
    const payload = {
      companyName: companyData.companyName,
      legalInfo: companyData.legalInfo,
      legalRepresentative: companyData.legalRepresentative,
      operationalInfo: companyData.operationalInfo,
      paymentInfo: seller.paymentInfo
    };

    const sub = this.userService.updateCompanySellerAccount(this.user.id, payload).subscribe({
      next: () => {
        this.closeCompanyInfoModal();
        this.translateService.get('seller_profile.company_info_saved').subscribe(message => {
          this.toast.success(message);
        });
        // Reload seller account to get updated data
        this.loadSellerAccount();
      },
      error: () => {
        this.translateService.get('seller_profile.company_info_save_error').subscribe(message => {
          this.toast.error(message);
        });
      }
    });

    this.subscriptions.push(sub);
  }

  onSavePayment(paymentInfo: any): void {
    console.log('[PAYMENT] onSavePayment called with:', paymentInfo);
    console.log('[PAYMENT] this.user:', this.user);
    console.log('[PAYMENT] this.sellerDetailsFromEndpoint:', this.sellerDetailsFromEndpoint);
    console.log('[PAYMENT] isPrivateSeller:', this.isPrivateSeller);
    console.log('[PAYMENT] isCompanySeller:', this.isCompanySeller);

    if (!this.user?.id || !this.sellerDetailsFromEndpoint) {
      console.error('[PAYMENT] ERROR: User data not available');
      this.toast.error('User data not available');
      return;
    }

    let payload: any;
    let updateCall: any;

    if (this.isPrivateSeller) {
      // Build payload for private seller with existing firstName/lastName and new paymentInfo
      payload = {
        firstName: this.sellerDetailsFromEndpoint.firstName,
        lastName: this.sellerDetailsFromEndpoint.lastName,
        paymentInfo: paymentInfo
      };
      console.log('[PAYMENT] Private seller payload:', payload);
      updateCall = this.userService.updatePrivateSellerAccount(this.user.id, payload);
    } else if (this.isCompanySeller) {
      // Build payload for company seller with all existing company data and new paymentInfo
      const seller = this.sellerDetailsFromEndpoint;
      console.log('[PAYMENT] Company seller data:', seller);
      console.log('[PAYMENT] companyName:', seller.companyName);
      console.log('[PAYMENT] legalInfo:', seller.legalInfo);
      console.log('[PAYMENT] legalRepresentative:', seller.legalRepresentative);
      console.log('[PAYMENT] operationalInfo:', seller.operationalInfo);
      
      payload = {
        companyName: seller.companyName,
        legalInfo: seller.legalInfo,
        legalRepresentative: seller.legalRepresentative,
        operationalInfo: seller.operationalInfo,
        paymentInfo: paymentInfo
      };
      console.log('[PAYMENT] Company seller payload:', JSON.stringify(payload, null, 2));
      updateCall = this.userService.updateCompanySellerAccount(this.user.id, payload);
    } else {
      console.error('[PAYMENT] ERROR: Unknown seller type');
      this.toast.error('Unknown seller type');
      return;
    }

    console.log('[PAYMENT] Making API call...');
    const sub = updateCall.subscribe({
      next: (response: any) => {
        console.log('[PAYMENT] API call SUCCESS:', response);
        this.closePaymentModal();
        this.translateService.get('seller_profile.payment_saved').subscribe(message => {
          this.toast.success(message);
        });
        // Reload seller account to get updated data
        this.loadSellerAccount();
      },
      error: (error: any) => {
        console.error('[PAYMENT] API call ERROR:', error);
        console.error('[PAYMENT] Error status:', error?.status);
        console.error('[PAYMENT] Error message:', error?.message);
        console.error('[PAYMENT] Error body:', error?.error);
        this.translateService.get('seller_profile.payment_save_error').subscribe(message => {
          this.toast.error(message);
        });
      }
    });

    this.subscriptions.push(sub);
  }

  // Carrier Payment Edit Modal Methods
  openEditCarrierPaymentModal(): void {
    this.existingPaymentInfo = this.carrierDetailsFromEndpoint?.paymentInfo;
    this.showPaymentModal = true;
  }

  onSaveCarrierPayment(paymentInfo: any): void {
    if (!this.user?.id || !this.carrierDetailsFromEndpoint) {
      this.toast.error('Carrier data not available');
      return;
    }

    if (this.isPrivateCarrier) {
      // Delivery Agent: keep existing firstName/lastName, update paymentInfo only
      const payload = {
        firstName: this.carrierDetailsFromEndpoint.firstName,
        lastName: this.carrierDetailsFromEndpoint.lastName,
        paymentInfo: paymentInfo
      };

      const sub = this.userService.updateLogisticAgent(this.user.id, payload).subscribe({
        next: () => {
          this.closePaymentModal();
          this.translateService.get('seller_profile.payment_saved').subscribe(message => {
            this.toast.success(message);
          });
          // Reload carrier details to get updated data
          this.loadCarrierDetails();
        },
        error: () => {
          this.translateService.get('seller_profile.payment_save_error').subscribe(message => {
            this.toast.error(message);
          });
        }
      });

      this.subscriptions.push(sub);
    } else if (this.isCompanyCarrier) {
      // Logistics Company: keep all existing company data, update paymentInfo only
      const carrier = this.carrierDetailsFromEndpoint;
      const payload = {
        companyName: carrier.companyName,
        legalInfo: carrier.legalInfo,
        legalRepresentative: carrier.legalRepresentative,
        operationalInfo: carrier.operationalInfo,
        paymentInfo: paymentInfo
      };

      const sub = this.userService.updateLogisticCompany(this.user.id, payload).subscribe({
        next: () => {
          this.closePaymentModal();
          this.translateService.get('seller_profile.payment_saved').subscribe(message => {
            this.toast.success(message);
          });
          // Reload carrier details to get updated data
          this.loadCarrierDetails();
        },
        error: () => {
          this.translateService.get('seller_profile.payment_save_error').subscribe(message => {
            this.toast.error(message);
          });
        }
      });

      this.subscriptions.push(sub);
    }
  }

  // Carrier Company Info Edit Modal Methods
  openEditCarrierCompanyInfoModal(): void {
    this.existingCompanyInfo = this.carrierDetailsFromEndpoint;
    this.showCompanyInfoModal = true;
  }

  onSaveCarrierCompanyInfo(companyData: any): void {
    if (!this.user?.id || !this.carrierDetailsFromEndpoint) {
      this.toast.error('Carrier data not available');
      return;
    }

    // Build payload with new company data and existing paymentInfo
    const carrier = this.carrierDetailsFromEndpoint;
    const payload = {
      companyName: companyData.companyName,
      legalInfo: companyData.legalInfo,
      legalRepresentative: companyData.legalRepresentative,
      operationalInfo: companyData.operationalInfo,
      paymentInfo: carrier.paymentInfo
    };

    const sub = this.userService.updateLogisticCompany(this.user.id, payload).subscribe({
      next: () => {
        this.closeCompanyInfoModal();
        this.translateService.get('seller_profile.company_info_saved').subscribe(message => {
          this.toast.success(message);
        });
        // Reload carrier details to get updated data
        this.loadCarrierDetails();
      },
      error: () => {
        this.translateService.get('seller_profile.company_info_save_error').subscribe(message => {
          this.toast.error(message);
        });
      }
    });

    this.subscriptions.push(sub);
  }

  // Modal methods for phone verification
  openVerificationModal(): void {
    // If we already have a token, just show the modal without making a new request
    if (this.verificationToken) {
      this.showVerificationModal = true;
      return;
    }

    this.requestVerificationCode();
  }

  closeVerificationModal(): void {
    this.showVerificationModal = false;
    this.verificationError = '';
    // Note: We keep the verificationToken so user can reopen the modal
  }

  requestVerificationCode(): void {
    if (!this.user?.id || !this.sellerDetailsFromEndpoint?.paymentInfo) {
      this.toast.error('Payment information not available');
      return;
    }

    const paymentInfo = this.sellerDetailsFromEndpoint.paymentInfo;
    const payload = {
      phoneNumberToVerify: paymentInfo.mobileMoneyNumber,
      resource: 'PaymentInfo',
      resourceId: paymentInfo.id
    };

    const sub = this.userService.requestPhoneVerificationCode(this.user.id, payload).subscribe({
      next: (response: any) => {
        if (response?.data?.token) {
          this.verificationToken = response.data.token;
          this.showVerificationModal = true;
          this.startResendCountdown();
        }
      },
      error: (error) => {
        if (error?.error?.errorCode === 'code_already_sent') {
          this.translateService.get('error.code_already_sent').subscribe(msg => this.toast.error(msg));
        } else {
          this.toast.error(error?.error?.message || 'Failed to send verification code');
        }
      }
    });

    this.subscriptions.push(sub);
  }

  onResendCode(): void {
    if (this.resendDisabled) return;

    // Request a new code
    this.requestVerificationCode();
  }

  onVerifyCode(code: string): void {
    if (!this.user?.id || !this.verificationToken) {
      this.toast.error('Verification session expired');
      return;
    }

    const payload = {
      token: this.verificationToken,
      code: code
    };

    const sub = this.userService.validateVerificationCode(this.user.id, payload).subscribe({
      next: () => {
        this.closeVerificationModal();
        this.verificationToken = null; // Clear token on success
        this.translateService.get('seller_profile.verification_success').subscribe(message => {
          this.toast.success(message);
        });
        // Reload seller account to get updated numberVerified status
        this.loadSellerAccount();
      },
      error: () => {
        this.verificationError = '';
        this.translateService.get('seller_profile.verification_error').subscribe(message => {
          this.verificationError = message;
        });
        // Clear all input boxes and focus first one
        if (this.verificationModal) {
          this.verificationModal.clearAndFocus();
        }
      }
    });

    this.subscriptions.push(sub);
  }

  // Carrier-specific phone verification methods
  openCarrierVerificationModal(): void {
    // If we already have a token, just show the modal without making a new request
    if (this.verificationToken) {
      this.showVerificationModal = true;
      return;
    }

    this.requestCarrierVerificationCode();
  }

  requestCarrierVerificationCode(): void {
    if (!this.user?.id || !this.carrierDetailsFromEndpoint?.paymentInfo) {
      this.toast.error('Payment information not available');
      return;
    }

    const paymentInfo = this.carrierDetailsFromEndpoint.paymentInfo;
    const payload = {
      phoneNumberToVerify: paymentInfo.mobileMoneyNumber,
      resource: 'PaymentInfo',
      resourceId: paymentInfo.id
    };

    const sub = this.userService.requestPhoneVerificationCode(this.user.id, payload).subscribe({
      next: (response: any) => {
        if (response?.data?.token) {
          this.verificationToken = response.data.token;
          this.showVerificationModal = true;
          this.startResendCountdown();
        }
      },
      error: (error) => {
        if (error?.error?.errorCode === 'code_already_sent') {
          this.translateService.get('error.code_already_sent').subscribe(msg => this.toast.error(msg));
        } else {
          this.toast.error(error?.error?.message || 'Failed to send verification code');
        }
      }
    });

    this.subscriptions.push(sub);
  }

  onResendCarrierCode(): void {
    if (this.resendDisabled) return;

    // Request a new code
    this.requestCarrierVerificationCode();
  }

  onVerifyCarrierCode(code: string): void {
    if (!this.user?.id || !this.verificationToken) {
      this.toast.error('Verification session expired');
      return;
    }

    const payload = {
      token: this.verificationToken,
      code: code
    };

    const sub = this.userService.validateVerificationCode(this.user.id, payload).subscribe({
      next: () => {
        this.closeVerificationModal();
        this.verificationToken = null; // Clear token on success
        this.translateService.get('carrier_profile.verification_success').subscribe(message => {
          this.toast.success(message);
        });
        // Reload carrier details to get updated numberVerified status
        this.loadCarrierDetails();
      },
      error: () => {
        this.verificationError = '';
        this.translateService.get('carrier_profile.verification_error').subscribe(message => {
          this.verificationError = message;
        });
        // Clear all input boxes and focus first one
        if (this.verificationModal) {
          this.verificationModal.clearAndFocus();
        }
      }
    });

    this.subscriptions.push(sub);
  }

  startResendCountdown(): void {
    this.resendDisabled = true;
    this.resendCountdown = 600; // 10 minutes in seconds

    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }

    this.countdownSubscription = interval(1000)
      .pipe(takeWhile(() => this.resendCountdown > 0))
      .subscribe({
        next: () => {
          this.resendCountdown--;
        },
        complete: () => {
          this.resendDisabled = false;
        }
      });

    this.subscriptions.push(this.countdownSubscription);
  }

  // Helper getters for user type detection
  get isPrivateSeller(): boolean {
    return this.user?.role === 'seller' && this.sellerDetailsFromEndpoint?.sellerType === 'private';
  }

  get isCompanySeller(): boolean {
    return this.user?.role === 'seller' && this.sellerDetailsFromEndpoint?.sellerType === 'company';
  }

  get isBuyer(): boolean {
     return this.user?.role === 'buyer' || this.user?.role === 'user';
  }

  get isSellerProfileIncomplete(): boolean {
    return this.user?.role === 'seller' && this.sellerDetailsFromEndpoint === null;
  }

  get isCarrierProfileIncomplete(): boolean {
    return this.user?.role === 'carrier' && this.carrierDetailsFromEndpoint === null;
  }

  get isCarrier(): boolean {
    return this.user?.role === 'carrier' && this.carrierDetailsFromEndpoint !== null;
  }

  get isPrivateCarrier(): boolean {
    return this.carrierDetailsFromEndpoint?.carrierType === 'private';
  }

  get isCompanyCarrier(): boolean {
    return this.carrierDetailsFromEndpoint?.carrierType === 'company';
  }

  get isCarrierPending(): boolean {
    return this.carrierDetailsFromEndpoint?.status === 'PENDING';
  }

  get isCarrierVerified(): boolean {
    return this.carrierDetailsFromEndpoint?.status === 'VERIFIED';
  }

  get isCarrierRejected(): boolean {
    return this.carrierDetailsFromEndpoint?.status === 'REJECTED';
  }

  get carrierRejectionReason(): string {
    return this.carrierDetailsFromEndpoint?.reason || '';
  }

  // Helper getters for carrier phone verification - uses endpoint data
  get needsCarrierPhoneVerification(): boolean {
    return this.carrierDetailsFromEndpoint?.paymentInfo?.paymentType === 'MOBILE_MONEY' &&
           this.carrierDetailsFromEndpoint?.paymentInfo?.numberVerified === false;
  }

  // Helper getter to check if carrier can delete documents - uses endpoint data
  get canDeleteCarrierDocuments(): boolean {
    const status = this.carrierDetailsFromEndpoint?.status;
    return status === 'PENDING' || status === 'REJECTED';
  }

  get canEditCarrierInfo(): boolean {
    // Can edit carrier info if PENDING or REJECTED (not when COMPLETED or VERIFIED)
    const status = this.carrierDetailsFromEndpoint?.status;
    return status === 'PENDING' || status === 'REJECTED';
  }

  // Helper getter to check if carrier has documents
  get hasCarrierDocuments(): boolean {
    return this.carrierDetailsFromEndpoint?.documents && 
           this.carrierDetailsFromEndpoint.documents.length > 0;
  }

  // Check if carrier can submit for approval
  get canCarrierSubmitForApproval(): boolean {
    const status = this.carrierDetailsFromEndpoint?.status;
    // Can submit if PENDING or REJECTED (for resubmission)
    const statusOk = status === 'PENDING' || status === 'REJECTED';

    if (!statusOk) {
      return false;
    }

    // If mobile money, must be verified
    const phoneVerified = this.carrierDetailsFromEndpoint?.paymentInfo?.paymentType !== 'MOBILE_MONEY' ||
                          this.carrierDetailsFromEndpoint?.paymentInfo?.numberVerified === true;

    if (!phoneVerified) {
      return false;
    }

    // For company carriers, all 4 required documents must be present
    if (this.isCompanyCarrier) {
      return this.missingCompanyDocuments.length === 0;
    }

    // For private carriers, at least one document is needed
    return this.carrierDetailsFromEndpoint?.documents &&
           this.carrierDetailsFromEndpoint.documents.length > 0;
  }

  // Check if carrier document is missing
  get isCarrierDocumentMissing(): boolean {
    const status = this.carrierDetailsFromEndpoint?.status;
    // Check documents for PENDING or REJECTED (so they can fix and resubmit)
    if (status !== 'PENDING' && status !== 'REJECTED') {
      return false;
    }

    // For company carriers, check if any required documents are missing
    if (this.isCompanyCarrier) {
      return this.missingCompanyDocuments.length > 0;
    }

    // For private carriers, check if no documents at all
    return !this.carrierDetailsFromEndpoint?.documents ||
           this.carrierDetailsFromEndpoint.documents.length === 0;
  }

  // Check if carrier phone verification is required
  get isCarrierPhoneVerificationRequired(): boolean {
    const hasMobileMoney = this.carrierDetailsFromEndpoint?.paymentInfo?.paymentType === 'MOBILE_MONEY';
    const notVerified = !this.carrierDetailsFromEndpoint?.paymentInfo?.numberVerified;
    return this.carrierDetailsFromEndpoint?.status === 'PENDING' && hasMobileMoney && notVerified;
  }

  // Check if a specific company document type is uploaded
  isCompanyDocumentUploaded(docType: string): boolean {
    return this.carrierDetailsFromEndpoint?.documents?.some((doc: any) => doc.documentType === docType) ?? false;
  }

  // Get uploaded company document by type
  getCompanyDocumentByType(docType: string): any {
    return this.carrierDetailsFromEndpoint?.documents?.find((doc: any) => doc.documentType === docType);
  }

  // Check which company documents are missing
  get missingCompanyDocuments(): string[] {
    const requiredDocs = ['IdCard', 'Passport', 'RegistrationCertificate', 'TaxComplianceCertificate', 'RegistrationOfIncorporation'];
    const uploadedTypes = this.carrierDetailsFromEndpoint?.documents?.map((doc: any) => doc.documentType) || [];
    
    // Check if identity document is uploaded (either IdCard or Passport)
    const hasIdentity = uploadedTypes.includes('IdCard') || uploadedTypes.includes('Passport');
    
    const missing: string[] = [];
    
    if (!hasIdentity) {
      missing.push('identity');
    }
    if (!uploadedTypes.includes('RegistrationCertificate')) {
      missing.push('registration');
    }
    if (!uploadedTypes.includes('TaxComplianceCertificate')) {
      missing.push('tax');
    }
    if (!uploadedTypes.includes('RegistrationOfIncorporation')) {
      missing.push('incorporation');
    }
    
    return missing;
  }

  get canBecomeSeller(): boolean {
    // User or buyer can become seller if they don't have a seller profile yet and are not a carrier
    const role = this.user?.role?.toLowerCase();
    return (role === 'user' || role === 'buyer') && this.sellerDetailsFromEndpoint === null && this.carrierDetailsFromEndpoint === null;
  }

  get canBecomeCarrier(): boolean {
    // User or buyer can become carrier if they don't have a carrier profile yet and are not a seller
    const role = this.user?.role?.toLowerCase();
    return (role === 'user' || role === 'buyer') && this.carrierDetailsFromEndpoint === null && this.sellerDetailsFromEndpoint === null;
  }

  // Helper getters for seller status
  get isPending(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'COMPLETED';
  }

  get isApproved(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'VERIFIED';
  }

  get isRejected(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'REJECTED';
  }

  get rejectionReason(): string {
    return this.sellerDetailsFromEndpoint?.reason || '';
  }

  get canDeleteDocuments(): boolean {
    // Can only delete documents if PENDING or REJECTED (not when COMPLETED or VERIFIED)
    const status = this.sellerDetailsFromEndpoint?.status;
    return status === 'PENDING' || status === 'REJECTED';
  }

  get canEditSellerInfo(): boolean {
    // Can edit info if PENDING or REJECTED (not when COMPLETED or VERIFIED)
    const status = this.sellerDetailsFromEndpoint?.status;
    return status === 'PENDING' || status === 'REJECTED';
  }

  get canSubmitForApproval(): boolean {
    // Can submit if status is PENDING or REJECTED (for resubmission)
    // AND has payment info
    // AND phone is verified (if mobile money)
    // AND all required documents are present
    const status = this.sellerDetailsFromEndpoint?.status;
    const statusOk = status === 'PENDING' || status === 'REJECTED';

    // Must have payment info
    const hasPaymentInfo = !!this.sellerDetailsFromEndpoint?.paymentInfo?.paymentType;
    if (!hasPaymentInfo) {
      return false;
    }

    // If mobile money, must be verified
    const phoneVerified = !this.needsPhoneVerification;

    // Check documents based on seller type
    let hasAllDocs = false;
    if (this.isCompanySeller) {
      const hasIdentityDoc = this.isCompanySellerDocumentUploaded('IdCard') || this.isCompanySellerDocumentUploaded('Passport');
      const hasRegistrationDoc = this.isCompanySellerDocumentUploaded('RegistrationCertificate');
      const hasTaxDoc = this.isCompanySellerDocumentUploaded('TaxComplianceCertificate');
      const hasIncorporationDoc = this.isCompanySellerDocumentUploaded('RegistrationOfIncorporation');
      hasAllDocs = hasIdentityDoc && hasRegistrationDoc && hasTaxDoc && hasIncorporationDoc;
    } else if (this.isPrivateSeller) {
      hasAllDocs = this.isPrivateSellerDocumentUploaded('IdCard') || this.isPrivateSellerDocumentUploaded('Passport');
    }

    return statusOk && phoneVerified && hasAllDocs;
  }

  get isPaymentInfoMissing(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'PENDING' && !this.sellerDetailsFromEndpoint?.paymentInfo?.paymentType;
  }

  get isPhoneVerificationRequired(): boolean {
    const hasPaymentInfo = !!this.sellerDetailsFromEndpoint?.paymentInfo?.paymentType;
    return this.sellerDetailsFromEndpoint?.status === 'PENDING' && hasPaymentInfo && this.needsPhoneVerification;
  }

  get isRegistrationDocMissing(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'PENDING' && this.isCompanySeller && !this.isCompanySellerDocumentUploaded('RegistrationCertificate');
  }

  get isIdentityDocMissing(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'PENDING' && this.isCompanySeller && !this.isCompanySellerDocumentUploaded('IdCard') && !this.isCompanySellerDocumentUploaded('Passport');
  }

  get isTaxDocMissing(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'PENDING' && this.isCompanySeller && !this.isCompanySellerDocumentUploaded('TaxComplianceCertificate');
  }

  get isIncorporationDocMissing(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'PENDING' && this.isCompanySeller && !this.isCompanySellerDocumentUploaded('RegistrationOfIncorporation');
  }

  get isPrivateSellerIdentityDocMissing(): boolean {
    return this.sellerDetailsFromEndpoint?.status === 'PENDING' && this.isPrivateSeller && !this.isPrivateSellerDocumentUploaded('IdCard') && !this.isPrivateSellerDocumentUploaded('Passport');
  }

  // Check if a specific private seller document type is uploaded
  isPrivateSellerDocumentUploaded(docType: string): boolean {
    return this.sellerDetailsFromEndpoint?.documents?.some((doc: any) => doc.documentType === docType) ?? false;
  }

  // Get uploaded private seller document by type
  getPrivateSellerDocumentByType(docType: string): any {
    return this.sellerDetailsFromEndpoint?.documents?.find((doc: any) => doc.documentType === docType);
  }

  // Check if a specific company seller document type is uploaded
  isCompanySellerDocumentUploaded(docType: string): boolean {
    return this.sellerDetailsFromEndpoint?.documents?.some((doc: any) => doc.documentType === docType) ?? false;
  }

  // Get uploaded company seller document by type
  getCompanySellerDocumentByType(docType: string): any {
    return this.sellerDetailsFromEndpoint?.documents?.find((doc: any) => doc.documentType === docType);
  }

  // Document upload modal methods
  openUploadModal(documentType: 'registration' | 'identity'): void {
    console.log('[UPLOAD-MODAL-OPEN] Opening upload modal for:', documentType);
    console.log('[UPLOAD-MODAL-OPEN] sellerDetailsFromEndpoint:', this.sellerDetailsFromEndpoint);
    console.log('[UPLOAD-MODAL-OPEN] Seller ID:', this.sellerDetailsFromEndpoint?.id);
    console.log('[UPLOAD-MODAL-OPEN] Seller Type:', this.sellerDetailsFromEndpoint?.sellerType);
    this.uploadDocumentType = documentType;
    this.sellerId = this.sellerDetailsFromEndpoint?.id || '';
    console.log('[UPLOAD-MODAL-OPEN] Set sellerId to:', this.sellerId);
    this.showUploadModal = true;
  }

  closeUploadModal(): void {
    this.showUploadModal = false;
    this.uploadDocumentType = 'registration';
    this.sellerId = '';
  }

  onDocumentUploaded(docPath: string): void {
    console.log('[UPLOAD-COMPLETE] Document uploaded, docPath:', docPath);
    console.log('[UPLOAD-COMPLETE] Upload type:', this.uploadDocumentType);
    console.log('[UPLOAD-COMPLETE] isPrivateSeller:', this.isPrivateSeller);
    console.log('[UPLOAD-COMPLETE] isCompanySeller:', this.isCompanySeller);
    // Update the user data with the new document path
    if (this.uploadDocumentType === 'registration' && this.sellerDetailsFromEndpoint?.legalInfo) {
      this.sellerDetailsFromEndpoint.legalInfo.liDocPath = docPath;
    } else if (this.uploadDocumentType === 'identity') {
      if (this.isCompanySeller && this.sellerDetailsFromEndpoint?.legalRepresentative) {
        this.sellerDetailsFromEndpoint.legalRepresentative.lrDocPath = docPath;
      }
      // For private sellers with documents array, page reload will fetch fresh data
    }
    
    this.translateService.get('seller_profile.document_uploaded').subscribe(message => {
      this.toast.success(message);
    });
    
    this.closeUploadModal();
    
    // Note: Page reload is now handled by the modal when autoReload is true
  }

  // Carrier document upload methods
  openCarrierUploadModal(): void {
    this.uploadDocumentType = 'identity';
    this.sellerId = this.carrierDetailsFromEndpoint?.id || '';
    this.showUploadModal = true;
  }

  onCarrierDocumentUploaded(docPath: string): void {
    // Reload carrier details to get the updated documents list
    this.loadCarrierDetails();
    
    this.translateService.get('carrier_profile.document_uploaded').subscribe(message => {
      this.toast.success(message);
    });
    
    this.closeUploadModal();
  }

  // Helper method to check if phone verification is needed
  get needsPhoneVerification(): boolean {
    return this.sellerDetailsFromEndpoint?.paymentInfo?.paymentType === 'MOBILE_MONEY' &&
           this.sellerDetailsFromEndpoint?.paymentInfo?.numberVerified === false;
  }

  // Helper method to get human-readable payment type label
  getPaymentTypeLabel(): string {
    if (!this.sellerDetailsFromEndpoint?.paymentInfo) {
      return '';
    }
    const paymentType = this.sellerDetailsFromEndpoint.paymentInfo.paymentType;
    if (paymentType === 'MOBILE_MONEY') {
      return 'seller_profile.mobile_money';
    } else if (paymentType === 'BANK') {
      return 'seller_profile.bank_transfer';
    }
    return '';
  }

  // Helper method to get human-readable mobile provider label
  getMobileProviderLabel(): string {
    if (!this.sellerDetailsFromEndpoint?.paymentInfo?.mobileMoneyProvider) {
      return '';
    }
    const provider = this.sellerDetailsFromEndpoint.paymentInfo.mobileMoneyProvider;
    if (provider === 'MTN_MOMO') {
      return 'seller_profile.mtn_momo';
    } else if (provider === 'ORANGE_MONEY') {
      return 'seller_profile.orange_money';
    }
    return '';
  }

  loadProfilePicture() {
    // Placeholder for future implementation
  }

  downloadDocument(filePath: string): void {
    if (!filePath) {
      this.translateService.get('seller_profile.document_not_available').subscribe(message => {
        this.toast.error(message);
      });
      return;
    }

    // Extract the actual filename from the path
    const actualFileName = filePath.split('/').pop() || 'document';

    this.documentService.downloadDocument(filePath).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = actualFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        // Error handling removed - CORS issues may trigger error but download still works via browser extensions
      }
    });
  }

  downloadSellerDocumentById(documentId: string): void {
    if (!documentId) {
      this.translateService.get('seller_profile.document_not_available').subscribe(message => {
        this.toast.error(message);
      });
      return;
    }

    // Find the document to get contentType and determine correct extension
    const sellerDoc = this.sellerDetailsFromEndpoint?.documents?.find((d: any) => d.id === documentId);
    const contentType = sellerDoc?.contentType || 'application/pdf';
    
    // Determine file extension from contentType
    let extension = 'pdf';
    if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
      extension = 'jpg';
    } else if (contentType.includes('image/png')) {
      extension = 'png';
    }
    
    // Get filename from documentPath
    let fileName = `document.${extension}`;
    if (sellerDoc?.documentPath) {
      const pathParts = sellerDoc.documentPath.split('/');
      const originalFileName = pathParts[pathParts.length - 1];
      if (originalFileName) {
        const nameWithoutExt = originalFileName.substring(0, originalFileName.lastIndexOf('.')) || originalFileName;
        fileName = `${nameWithoutExt}.${extension}`;
      }
    }

    this.documentService.downloadDocumentById(documentId).subscribe({
      next: (blob: Blob) => {
        const correctBlob = new Blob([blob], { type: contentType });
        const url = window.URL.createObjectURL(correctBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        // Error handling removed - CORS issues may trigger error but download still works via browser extensions
      }
    });
  }

  downloadCarrierDocument(documentId: string): void {
    if (!documentId) {
      this.translateService.get('carrier_profile.document_not_available').subscribe(message => {
        this.toast.error(message);
      });
      return;
    }

    // Find the document to get contentType and determine correct extension
    const carrierDoc = this.carrierDetailsFromEndpoint?.documents?.find((d: any) => d.id === documentId);
    const contentType = carrierDoc?.contentType || 'application/pdf';
    
    // Determine file extension from contentType
    let extension = 'pdf';
    if (contentType.includes('png')) {
      extension = 'png';
    } else if (contentType.includes('jpeg') || contentType.includes('jpg')) {
      extension = 'jpg';
    }
    
    // Extract original filename from documentPath if available
    let fileName = `document.${extension}`;
    if (carrierDoc?.documentPath) {
      const pathParts = carrierDoc.documentPath.split('/');
      const originalFileName = pathParts[pathParts.length - 1];
      if (originalFileName) {
        // Replace extension with correct one based on contentType
        const nameWithoutExt = originalFileName.substring(0, originalFileName.lastIndexOf('.')) || originalFileName;
        fileName = `${nameWithoutExt}.${extension}`;
      }
    }

    this.documentService.downloadDocumentById(documentId).subscribe({
      next: (blob: Blob) => {
        // Create blob with correct MIME type
        const correctBlob = new Blob([blob], { type: contentType });
        const url = window.URL.createObjectURL(correctBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        // Error handling removed - CORS issues may trigger error but download still works via browser extensions
      }
    });
  }

  deleteDocument(filePath: string, documentType: 'registration' | 'identity'): void {
    if (!filePath) {
      this.translateService.get('seller_profile.document_not_available').subscribe(message => {
        this.toast.error(message);
      });
      return;
    }

    const title = documentType === 'registration' 
      ? this.translateService.instant('seller_profile.registration_document')
      : this.translateService.instant('seller_profile.identity_document');
    
    const message = this.translateService.instant('seller_profile.delete_confirmation');
    
    const deleteAction = () => {
      this.documentService.deleteDocument(filePath).subscribe({
        next: () => {
          // Clear the document path from user data based on seller type
          if (documentType === 'registration' && this.sellerDetailsFromEndpoint?.legalInfo) {
            this.sellerDetailsFromEndpoint.legalInfo.liDocPath = null;
          } else if (documentType === 'identity') {
            if (this.isCompanySeller && this.sellerDetailsFromEndpoint?.legalRepresentative) {
              this.sellerDetailsFromEndpoint.legalRepresentative.lrDocPath = null;
            } else if (this.isPrivateSeller && this.sellerDetailsFromEndpoint) {
              this.sellerDetailsFromEndpoint.legalDocPath = null;
            }
          }
          
          this.translateService.get('seller_profile.document_deleted').subscribe(message => {
            this.toast.success(message);
          });
        },
        error: () => {
          this.translateService.get('seller_profile.document_delete_error').subscribe(message => {
            this.toast.error(message);
          });
        }
      });
    };
    
    this.openConfirmModal(title, message, deleteAction);
  }

  deleteSellerDocumentById(documentId: string, documentType: string): void {
    if (!documentId) {
      this.translateService.get('seller_profile.document_not_available').subscribe(message => {
        this.toast.error(message);
      });
      return;
    }

    const title = this.translateService.instant('seller_profile.identity_document');
    const message = this.translateService.instant('seller_profile.delete_confirmation');
    
    const deleteAction = () => {
      this.documentService.deleteDocumentById(documentId).subscribe({
        next: () => {
          // Remove document from the array by filtering out the one with matching ID
          if (this.sellerDetailsFromEndpoint?.documents) {
            this.sellerDetailsFromEndpoint.documents = this.sellerDetailsFromEndpoint.documents.filter(
              (d: any) => d.id !== documentId
            );
          }
          
          this.translateService.get('seller_profile.document_deleted').subscribe(message => {
            this.toast.success(message);
          });
        },
        error: () => {
          this.translateService.get('seller_profile.document_delete_error').subscribe(message => {
            this.toast.error(message);
          });
        }
      });
    };
    
    this.openConfirmModal(title, message, deleteAction);
  }

  deleteCarrierDocument(documentId: string): void {
    console.log('Delete clicked, documentId:', documentId);
    console.log('Documents array:', this.carrierDetailsFromEndpoint?.documents);
    
    if (!documentId) {
      this.translateService.get('carrier_profile.document_not_available').subscribe(message => {
        this.toast.error(message);
      });
      return;
    }

    const title = this.translateService.instant('carrier_profile.identity_document');
    const message = this.translateService.instant('carrier_profile.delete_confirmation');
    
    const deleteAction = () => {
      this.documentService.deleteDocumentById(documentId).subscribe({
        next: () => {
          // Remove document from the array by filtering out the one with matching ID
          if (this.carrierDetailsFromEndpoint?.documents) {
            this.carrierDetailsFromEndpoint.documents = this.carrierDetailsFromEndpoint.documents.filter(
              (d: any) => d.id !== documentId
            );
          }
          
          this.translateService.get('carrier_profile.document_deleted').subscribe(message => {
            this.toast.success(message);
          });
        },
        error: () => {
          this.translateService.get('carrier_profile.document_delete_error').subscribe(message => {
            this.toast.error(message);
          });
        }
      });
    };
    
    this.openConfirmModal(title, message, deleteAction);
  }

  submitForApproval(): void {
    if (!this.user?.id || !this.sellerDetailsFromEndpoint) {
      this.toast.error('User data not available');
      return;
    }

    // Check if phone verification is required
    if (this.needsPhoneVerification) {
      this.translateService.get('seller_profile.phone_verification_required').subscribe(message => {
        this.toast.error(message);
      });
      return;
    }

    // Check if documents are present based on seller type
    if (this.isCompanySeller) {
      const hasIdentityDoc = this.isCompanySellerDocumentUploaded('IdCard') || this.isCompanySellerDocumentUploaded('Passport');
      const hasRegistrationDoc = this.isCompanySellerDocumentUploaded('RegistrationCertificate');
      const hasTaxDoc = this.isCompanySellerDocumentUploaded('TaxComplianceCertificate');
      const hasIncorporationDoc = this.isCompanySellerDocumentUploaded('RegistrationOfIncorporation');

      if (!hasRegistrationDoc) {
        this.translateService.get('seller_profile.missing_registration_doc').subscribe(message => {
          this.toast.error(message);
        });
        return;
      }
      if (!hasIdentityDoc) {
        this.translateService.get('seller_profile.missing_identity_doc').subscribe(message => {
          this.toast.error(message);
        });
        return;
      }
      if (!hasTaxDoc) {
        this.translateService.get('seller_profile.missing_tax_doc').subscribe(message => {
          this.toast.error(message);
        });
        return;
      }
      if (!hasIncorporationDoc) {
        this.translateService.get('seller_profile.missing_incorporation_doc').subscribe(message => {
          this.toast.error(message);
        });
        return;
      }
    } else if (this.isPrivateSeller) {
      const hasIdentityDoc = this.isPrivateSellerDocumentUploaded('IdCard') || this.isPrivateSellerDocumentUploaded('Passport');
      if (!hasIdentityDoc) {
        this.translateService.get('seller_profile.missing_identity_doc').subscribe(message => {
          this.toast.error(message);
        });
        return;
      }
    }

    // Build payload with all seller data
    const seller = this.sellerDetailsFromEndpoint;
    let payload: any;

    if (this.isPrivateSeller) {
      payload = {
        firstName: seller.firstName,
        lastName: seller.lastName,
        paymentInfo: seller.paymentInfo
      };
    } else if (this.isCompanySeller) {
      payload = {
        companyName: seller.companyName,
        legalInfo: seller.legalInfo,
        legalRepresentative: seller.legalRepresentative,
        operationalInfo: seller.operationalInfo,
        paymentInfo: seller.paymentInfo
      };
    } else {
      this.toast.error('Unknown seller type');
      return;
    }

    const sub = this.userService.completeSellerAccount(this.user.id, payload).subscribe({
      next: (response: any) => {
        // Update the seller status from the backend response (COMPLETED or UNDER_REVIEW)
        if (this.sellerDetailsFromEndpoint && response?.data?.status) {
          this.sellerDetailsFromEndpoint.status = response.data.status;
        }
        
        this.translateService.get('seller_profile.submitted_for_approval').subscribe(message => {
          this.toast.success(message);
        });
      },
      error: (error: any) => {
        console.error('Error submitting for approval:', error);
        this.translateService.get('seller_profile.submit_approval_error').subscribe(message => {
          this.toast.error(message);
        });
      }
    });

    this.subscriptions.push(sub);
  }

  // Carrier submit for approval method
  submitCarrierForApproval(): void {
    if (!this.user?.id || !this.carrierDetailsFromEndpoint) {
      this.toast.error('Carrier data not available');
      return;
    }

    // Check requirements
    if (!this.canCarrierSubmitForApproval) {
      this.translateService.get('carrier_profile.complete_requirements').subscribe(message => {
        this.toast.error(message);
      });
      return;
    }

    // Build payload
    const payload = {
      firstName: this.carrierDetailsFromEndpoint.firstName,
      lastName: this.carrierDetailsFromEndpoint.lastName,
      paymentInfo: this.carrierDetailsFromEndpoint.paymentInfo
    };

    const sub = this.userService.completeCarrierAccount(this.user.id, payload).subscribe({
      next: (response: any) => {
        // Update the carrier status from the backend response
        if (this.carrierDetailsFromEndpoint && response?.data?.status) {
          this.carrierDetailsFromEndpoint.status = response.data.status;
        }
        
        this.translateService.get('carrier_profile.submitted_for_approval').subscribe(message => {
          this.toast.success(message);
        });
      },
      error: (error: any) => {
        console.error('Error submitting carrier for approval:', error);
        this.translateService.get('carrier_profile.submit_approval_error').subscribe(message => {
          this.toast.error(message);
        });
      }
    });

    this.subscriptions.push(sub);
  }

  // Company Document Upload Methods
  openCompanyDocumentUploadModal(docType: 'identity' | 'registration' | 'tax' | 'incorporation'): void {
    this.currentUploadDocType = docType;
    this.companyDocumentFile = null;
    this.companyTaxDeliveryDate = '';
    this.showCompanyDocumentUploadModal = true;
  }

  closeCompanyDocumentUploadModal(): void {
    this.showCompanyDocumentUploadModal = false;
    this.companyDocumentFile = null;
    this.companyTaxDeliveryDate = '';
    this.companyDocumentUploading = false;
  }

  onCompanyDocumentFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.companyDocumentFile = input.files[0];
    }
  }

  uploadCompanyDocumentFromModal(): void {
    if (!this.companyDocumentFile) {
      this.toast.error('Please select a file');
      return;
    }

    // Check if tax certificate has delivery date
    if (this.currentUploadDocType === 'tax' && !this.companyTaxDeliveryDate) {
      this.toast.error('Please select the delivery date for the Tax Compliance Certificate');
      return;
    }

    this.companyDocumentUploading = true;

    // Map document types to API document types
    const documentTypeMap: { [key: string]: string } = {
      'identity': 'IdCard', // Default to IdCard, user can specify if needed
      'registration': 'RegistrationCertificate',
      'tax': 'TaxComplianceCertificate',
      'incorporation': 'RegistrationOfIncorporation'
    };

    const deliveredAt = this.currentUploadDocType === 'tax' ? this.companyTaxDeliveryDate : undefined;

    this.documentService.uploadDocument(
      this.companyDocumentFile,
      this.carrierDetailsFromEndpoint.id,
      'LogisticCompany',
      documentTypeMap[this.currentUploadDocType] as any,
      deliveredAt
    ).subscribe({
      next: () => {
        this.companyDocumentUploading = false;
        this.translateService.get('carrier_setup.success_upload').subscribe(msg => {
          this.toast.success(msg || 'Document uploaded successfully');
        });
        this.closeCompanyDocumentUploadModal();
        // Auto-reload page after successful upload
        setTimeout(() => { window.location.reload(); }, 1500);
      },
      error: (error) => {
        this.companyDocumentUploading = false;
        this.translateService.get('carrier_setup.error_upload').subscribe(msg => {
          this.toast.error(msg || 'Failed to upload document');
        });
      }
    });
  }

  openDeleteAccountModal(): void {
    this.deleteAccountConfirmationText = '';
    this.showDeleteAccountModal = true;
  }

  closeDeleteAccountModal(): void {
    this.showDeleteAccountModal = false;
    this.deleteAccountConfirmationText = '';
    this.deleteAccountLoading = false;
  }

  get isDeleteConfirmationValid(): boolean {
    return this.deleteAccountConfirmationText === this.DELETE_CONFIRMATION_WORD;
  }

  confirmDeleteAccount(): void {
    if (!this.isDeleteConfirmationValid) {
      return;
    }

    this.deleteAccountLoading = true;

    const sub = this.userService.deleteAccount().subscribe({
      next: () => {
        this.deleteAccountLoading = false;
        this.closeDeleteAccountModal();
        this.translateService.get('profile.delete_account_success').subscribe(message => {
          this.toast.success(message);
        });
        setTimeout(() => {
          this.userService.logout();
        }, 1500);
      },
      error: () => {
        this.deleteAccountLoading = false;
        this.translateService.get('profile.delete_account_error').subscribe(message => {
          this.toast.error(message);
        });
      }
    });

    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
  }
}
